import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import {
  checkPermissionMiddleware,
  checkPermissionMiddlewareFunction,
} from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { SepaMandat } from '../../sepa/entities/SepaMandat.js';
import { getMandatesEndedByUnlink } from '../../sepa/server/endMandatesForUnlinkedChild.js';
import { InvitationOptionsForType, type InviteType } from './invitationOptions.js';
import { resolveCreateAndLinkPerson } from './server/resolveCreateAndLinkPerson.js';
import { resolveInvitePersonToChild } from './server/resolveInvitePersonToChild.js';
import { resolveLinkPersonToChild } from './server/resolveLinkPersonToChild.js';
import { resolveManuallyConfirmInvitedPerson } from './server/resolveManuallyConfirmInvitedPerson.js';
import { resolveRemovePersonFromChild } from './server/resolveRemovePersonFromChild.js';
import { resolveRevokeInviteLink } from './server/resolveRevokeInviteLink.js';
import { resolveToggleInvitationType } from './server/resolveToggleInvitationType.js';

const invitePersonToChild = registerMutation({
  operationName: 'invitePersonToChild',
  type: () => Boolean,
  params: [
    { type: () => String, name: 'inviteType' },
    { type: () => ID, name: 'childID' },
    { type: () => String, name: 'invitedEmail' },
  ],
  middlewares: [
    options => {
      const inviteType = options.args.inviteType;
      return checkPermissionMiddleware(InvitationOptionsForType[inviteType].invitePermission)(options);
    },
  ],
  resolve: resolveInvitePersonToChild,
});

const manuallyConfirmInvitedPerson = registerMutation({
  operationName: 'manuallyConfirmInvitedPerson',
  type: () => Boolean,
  params: [
    { type: () => String, name: 'inviteType' },
    { type: () => ID, name: 'childID' },
    { type: () => String, name: 'invitedEmail' },
  ],
  middlewares: [
    options => {
      const inviteType = options.args.inviteType;
      return checkPermissionMiddleware(InvitationOptionsForType[inviteType].manualConfirmPermission)(options);
    },
  ],
  resolve: resolveManuallyConfirmInvitedPerson,
});

/** linking without an invitation is what confirming one by hand does, so it takes that permission */
const linkPersonToChild = registerMutation({
  operationName: 'linkPersonToChild',
  type: () => Boolean,
  params: [
    { type: () => String, name: 'inviteType' },
    { type: () => ID, name: 'childID' },
    { type: () => ID, name: 'personID' },
  ],
  middlewares: [
    options => {
      const inviteType = options.args.inviteType;
      return checkPermissionMiddleware(InvitationOptionsForType[inviteType].manualConfirmPermission)(options);
    },
  ],
  resolve: resolveLinkPersonToChild,
});

const createAndLinkPerson = registerMutation({
  operationName: 'createAndLinkPerson',
  type: () => String,
  params: [
    { type: () => String, name: 'inviteType' },
    { type: () => ID, name: 'linkedID' },
    { type: () => Boolean, name: 'asChild' },
    { type: () => String, name: 'email' },
    { type: () => String, name: 'vorname' },
    { type: () => String, name: 'nachname' },
    { type: () => String, name: 'schoolID' },
  ],
  middlewares: [
    checkPermissionMiddlewareFunction(
      (user, args) =>
        user.hasPermission(
          InvitationOptionsForType[args.inviteType as InviteType].manualConfirmPermission,
          ANY_CONTEXT,
        ) &&
        // a new child gets a role at the school, like a user created in the user list
        (!args.asChild || user.hasPermission(Permission.CREATE_USER, args.schoolID)),
    ),
  ],
  resolve: resolveCreateAndLinkPerson,
});

const toggleInvitationType = registerMutation({
  operationName: 'toggleInvitationType',
  type: () => Boolean,
  params: [
    { type: () => String, name: 'toggleToInviteType' },
    { type: () => ID, name: 'childID' },
    { type: () => ID, name: 'personID' },
  ],
  middlewares: [checkPermissionMiddleware(Permission.TOGGLE_SPENDER_INVITE_TYPE)],
  resolve: resolveToggleInvitationType,
});

const removePersonFromChild = registerMutation({
  operationName: 'removePersonFromChild',
  type: () => Boolean,
  params: [
    { type: () => String, name: 'inviteType' },
    { type: () => ID, name: 'childID' },
    { type: () => ID, name: 'personID' },
  ],
  middlewares: [
    options => {
      const inviteType = options.args.inviteType;
      return checkPermissionMiddleware(InvitationOptionsForType[inviteType].removePermission)(options);
    },
  ],
  resolve: resolveRemovePersonFromChild,
});

/** the mandates removing the link would end, for the confirmation to name them */
const getMandatesEndedByRemoval = registerQuery({
  operationName: 'getMandatesEndedByRemoval',
  type: () => [SepaMandat],
  params: [
    { type: () => String, name: 'inviteType' },
    { type: () => ID, name: 'childID' },
    { type: () => ID, name: 'personID' },
  ],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  middlewares: [
    options => {
      const inviteType = options.args.inviteType;
      return checkPermissionMiddleware(InvitationOptionsForType[inviteType].removePermission)(options);
    },
  ],
  resolve: (_inviteType: InviteType, childID: string, personID: string) =>
    getMandatesEndedByUnlink({ guardianID: personID, childID }),
});

const revokeInviteLink = registerMutation({
  operationName: 'revokeInviteLink',
  type: () => Boolean,
  params: [
    { type: () => String, name: 'inviteType' },
    { type: () => ID, name: 'childID' },
    { type: () => String, name: 'email' },
  ],
  middlewares: [
    options => {
      const inviteType = options.args.inviteType;
      return checkPermissionMiddleware(InvitationOptionsForType[inviteType].removePermission)(options);
    },
  ],
  resolve: resolveRevokeInviteLink,
});

export const SpenderSorgeberechtigteAPI = {
  invite: invitePersonToChild,
  manualConfirm: manuallyConfirmInvitedPerson,
  link: linkPersonToChild,
  createAndLink: createAndLinkPerson,
  toggleType: toggleInvitationType,
  remove: removePersonFromChild,
  mandatesEndedByRemoval: getMandatesEndedByRemoval,
  revoke: revokeInviteLink,
};
